import { Avatar, Box } from "@mui/material"
import { alpha } from "@mui/material/styles"
import {
  FocusHandler,
  useFocusable,
} from "@noriginmedia/norigin-spatial-navigation"
import _ from "lodash"
import React, {
  memo,
  ReactNode,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import ResizeObserverPoly from "resize-observer-polyfill"
import curatedContentLinkBg01 from "src/components/images/CuratedContentSlider/TeaserBg/01.svg"
import curatedContentLinkBg02 from "src/components/images/CuratedContentSlider/TeaserBg/02.svg"
import curatedContentLinkBg03 from "src/components/images/CuratedContentSlider/TeaserBg/03.svg"
import curatedContentLinkBg04 from "src/components/images/CuratedContentSlider/TeaserBg/04.svg"
import curatedContentLinkBg05 from "src/components/images/CuratedContentSlider/TeaserBg/05.svg"
import curatedContentLinkBg06 from "src/components/images/CuratedContentSlider/TeaserBg/06.svg"
import curatedContentLinkBg07 from "src/components/images/CuratedContentSlider/TeaserBg/07.svg"
import curatedContentLinkBg08 from "src/components/images/CuratedContentSlider/TeaserBg/08.svg"
import emptyAvatar from "src/components/images/empty_avatar.png"
import emptyGroup from "src/components/images/empty_group.png"
import empty from "src/components/images/empty.png"

import {
  SliderLayout,
  VideoType,
  VodConcertType,
} from "../../generated/graphql"
import useLiveConcertState from "../../hooks/use-live-concert-state"
import { useTransitions } from "../../hooks/use-transitions"
import useTranslate, { Namespaces } from "../../hooks/use-translate"
import { Reruns } from "../../screens/Detail/components/Reruns"
import { appStore } from "../../store/AppStore"
import { TranslationKey } from "../../types"
import { instantScroll, isTicketValid, s } from "../../utilities/common"
import {
  dimensions as windowDimensions,
  fontSizeFactor,
} from "../../utilities/constants"
import { theme, TI } from "../../utilities/theme"
import { DGText } from "../common/DGText"
import { ChevronRight } from "../common/icons/ChevronRight"
import { Image } from "../common/Image"
import "./Teaser.css"
import { ComposersIcon } from "../images/CuratedContentSlider/Icons/ComposersIcon"
import { ConductorsIcon } from "../images/CuratedContentSlider/Icons/ConductorsIcon"
import { EnsemblesIcon } from "../images/CuratedContentSlider/Icons/EnsemblesIcon"
import { EpochsIcon } from "../images/CuratedContentSlider/Icons/EpochsIcon"
import { GenresIcon } from "../images/CuratedContentSlider/Icons/GenresIcon"
import { PlaylistsIcon } from "../images/CuratedContentSlider/Icons/PlaylistsIcon"
import { SoloistsIcon } from "../images/CuratedContentSlider/Icons/SoloistsIcon"
import { VenuesIcon } from "../images/CuratedContentSlider/Icons/VenuesIcon"

import { SliderRef } from "./GenericSlider"
import { Livelabel } from "./Livelabel"
import { TeaserDimension, TeaserDimensions, TTeaser } from "./Slider"

export const curatedContentLinkBg = {
  composers: `url(${curatedContentLinkBg01})`,
  conductors: `url(${curatedContentLinkBg02})`,
  soloists: `url(${curatedContentLinkBg03})`,
  genres: `url(${curatedContentLinkBg04})`,
  epochs: `url(${curatedContentLinkBg05})`,
  ensembles: `url(${curatedContentLinkBg06})`,
  venues: `url(${curatedContentLinkBg07})`,
  playlist: `url(${curatedContentLinkBg08})`,
}

const ro =
  typeof ResizeObserver === "undefined" ? ResizeObserverPoly : ResizeObserver

export interface TeaserProps {
  index: number
  scrollView?: RefObject<SliderRef | null>
  scrollDirection: "vertical" | "horizontal" | "collection"
  me: string
  data?: TTeaser
  sliderId?: string
  dimensions: TeaserDimension
  colMargin?: boolean
  onPress: (data?: TTeaser, teaserIndex?: number) => void
  onFocus?: (index: number) => void
  onFocusOverride?: FocusHandler
  onBlur?: () => void
  ariaLabelPrefix?: string
  sliderLayout?: SliderLayout
}

export interface TeaserRef {
  reset: () => void
}

export const labelConfig: Record<string, TranslationKey<Namespaces.Default>> = {
  [VideoType.Documentary]: "api__type_media_documentary",
  [VideoType.Interview]: "api__type_media_interview",
  [VideoType.Short]: "api__type_media_short",
  [VodConcertType.Concert]: "api__type_media_concert",
  [VodConcertType.Opera]: "api__type_media_opera",
  [VodConcertType.Dance]: "api__type_media_dance",
  Live: "api__type_media_live",
  Free: "component__badge_free",
  Premiere: "api__type_media_live_premiere",
}

export const Label = ({
  color,
  backgroundColor,
  children,
  shadow = false,
}: {
  color: string
  backgroundColor: string
  children: ReactNode
  shadow?: boolean
}) => (
  <Box
    sx={{
      float: "left",
      p: s("0px 16px 0px 16px"),
      borderRadius: s("24px"),
      backgroundColor: backgroundColor,
      height: s("30px"),
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      boxShadow: shadow
        ? `${s("0px 2px 10px 0px")} rgba(0, 0, 0, 0.40)`
        : undefined,
    }}
  >
    <DGText
      tx={TI.Caption01tv}
      sx={{
        textTransform: "uppercase",
        color: color,
        textAlign: "center",
      }}
    >
      {children}
    </DGText>
  </Box>
)

export const Teaser = ({
  index,
  scrollView,
  scrollDirection,
  onBlur,
  ariaLabelPrefix,
  me,
  data,
  dimensions,
  colMargin = true,
  onPress,
  onFocus,
  onFocusOverride,
  sliderLayout,
}: TeaserProps) => {
  const { width, height, imageHeight, colSpacing } = useMemo(() => {
    return (
      TeaserDimensions[dimensions] ?? { width: 0, height: 0, imageHeight: 0 }
    )
  }, [dimensions])

  const onPressAction = useCallback(() => {
    if (data && index !== undefined) onPress(data, index)
    else onPress()
  }, [data])

  const t = useTranslate()
  const transition = useTransitions()

  const onFocusHandler: FocusHandler = useCallback(
    (layout, __, query) => {
      if (onFocusOverride) {
        onFocusOverride(layout, __, query)
        return
      }
      const { top: y } = layout
      if (
        !(query && query.noScroll) &&
        scrollView &&
        scrollView.current &&
        scrollView.current
      ) {
        if (onFocus) onFocus(index)
        // if getting auto-set on navigation, don't animate
        instantScroll(scrollView.current.element, query, transition)
        if (scrollDirection === "horizontal") {
          if (!query.noScrollTeaser) {
            if (
              !scrollView.current.element.scrollWidth ||
              scrollView.current.element.scrollWidth < windowDimensions.width
            )
              return
            // get style of slider
            const style = window.getComputedStyle(scrollView.current.element)
            // get width of slider
            const currentWidth = parseInt(style.width, 10)
            // this value represents the maximum displacement of the slider
            const maxSliderDisplacement = -(
              windowDimensions.width - currentWidth
            )
            // the target scroll displacement, but skipping the first element and scrolling after the 2nd from left
            const targetDisplacement =
              (parseInt(colSpacing, 10) + width) * Math.max(0, index - 1)

            // this aims to only set the index of the live title if there is actual displacement happening
            // "7" is an empirical error tolerance tested over responsive views
            if (targetDisplacement - 7 <= maxSliderDisplacement)
              scrollView.current?.setIndex(Math.max(0, index - 1))
            const scrollAmount =
              Math.min(maxSliderDisplacement, targetDisplacement) * -1
            scrollView.current.element.style.transform =
              "translate3d(" + scrollAmount + "px, 0, 0)"
          }
        } else if (scrollDirection === "vertical") {
          /*const scrollAmount =
            y > windowDimensions.height / 2 - height / 2
              ? -y + windowDimensions.height / 2 - height / 2
              : 0
          scrollView.current.style.transform =
            "translate(0, " + scrollAmount + "px)"*/
        } else if (scrollDirection === "collection") {
          if (!(query && query.noScroll)) {
            scrollView.current.element.style.transform =
              "translate3d(0, " +
              Math.min(0, -y + (windowDimensions.height - s(height)) / 2) +
              "px, 0)"
          }
        }
      }
    },
    [width, colSpacing],
  )

  const { ref, focused, focusSelf } = useFocusable({
    focusKey: index === 0 ? me + "first" : me + "teaser" + index,
    onEnterPress: onPressAction,
    onBlur,
    onFocus: onFocusHandler,
  })

  return (
    <Box
      ref={ref}
      tabIndex={0}
      aria-label={
        data
          ? (ariaLabelPrefix ? ariaLabelPrefix + ": " : "") +
            data.title +
            ". " +
            data.subtitle
          : t("slider__show_more")
      }
      onMouseEnter={() => focusSelf({ noScroll: true })}
      onMouseDown={onPressAction}
      className={
        data?.type === "CuratedContentLink" && !focused
          ? "hide_image"
          : undefined
      }
      sx={{
        backgroundColor:
          dimensions === TeaserDimension.WeeklyTop10
            ? focused
              ? alpha(theme.palette.secondary.main, 0.85)
              : alpha(theme.palette.secondary.main, 0.85)
            : focused
              ? alpha(theme.palette.secondary.variant, 0.85)
              : alpha(theme.palette.primary.variant, 0.85),
        height,
        width,
        display: "flex",
        flexDirection: "column",
        float: "left",
        ml: colMargin && index > 0 ? s("36px") : undefined,
        fontSize: s("30px", false, fontSizeFactor),
        fontWeight: "bold",
        //outline: focused ? "2px solid yellow" : undefined,
        zIndex: focused ? 2 : 1,
        boxShadow:
          dimensions === TeaserDimension.WeeklyTop10
            ? focused
              ? `${s("0px 9.32px 37.29px 0px")} rgba(0, 0, 0, 0.7)`
              : `${s("0px 8px 32px 0px")} rgba(0, 0, 0, 0.7)`
            : focused
              ? `${s("0px 30px 112px 0px")} rgba(0, 0, 0, 0.60)`
              : `${s("0px 8px 16px 0px")} rgba(0, 0, 0, 0.4)`,
        borderRadius:
          dimensions === TeaserDimension.WeeklyTop10 ? s("11px") : s("8px"),
        transform: focused ? "scale(1.14)" : undefined,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <MemoizedTeaser
        data={data}
        height={height}
        width={width}
        imageHeight={imageHeight}
        dimensions={dimensions}
        index={index}
        sliderLayout={sliderLayout}
      />
    </Box>
  )
}

type MemoProps = {
  data: TTeaser | undefined
  height: number
  width: number
  imageHeight: number
  dimensions: TeaserDimension
  index: number
  sliderLayout?: SliderLayout
}
const MemoizedTeaser = memo(
  ({
    data,
    height,
    width,
    imageHeight,
    dimensions,
    index,
    sliderLayout,
  }: MemoProps) => {
    const textboxes = useRef<HTMLElement>(null)
    const [subtitleLines, setSubtitleLines] = useState(1)
    const t = useTranslate()
    const t_common = useTranslate(Namespaces.Common)
    const currentUser = appStore((state) => state.currentUser)
    const { status } = useLiveConcertState(data?.liveConcert)

    const lockSubCalc = useRef(false)
    const calculateSubLines = () => {
      if (
        lockSubCalc.current ||
        !textboxes.current ||
        textboxes.current.getBoundingClientRect().height === 0
      )
        return

      setTimeout(() => {
        const check = !textboxes.current
          ? 0
          : textboxes.current.getBoundingClientRect().height <
              height - imageHeight - s(24)
            ? 1
            : textboxes.current.getBoundingClientRect().height >
                height - imageHeight - s(24)
              ? -1
              : 0
        if (check === -1) lockSubCalc.current = true
        setSubtitleLines((prevState) => Math.max(1, prevState + check))
      }, 50)
    }

    const runOnce = useRef(true)
    useEffect(() => {
      if (runOnce.current) {
        if (textboxes.current)
          new ro(calculateSubLines).observe(textboxes.current)
        runOnce.current = false
      }
    }, [])

    const CuratedContentLinkIcon = useCallback(() => {
      if (!data?.slugV2) return null
      switch (data.slugV2) {
        case "conductors":
          return <ConductorsIcon />
        case "composers":
          return <ComposersIcon />
        case "ensembles":
          return <EnsemblesIcon />
        case "epochs":
          return <EpochsIcon />
        case "genres":
          return <GenresIcon />
        case "playlist":
          return <PlaylistsIcon />
        case "soloists":
          return <SoloistsIcon />
        case "venues":
          return <VenuesIcon />
        default:
          return null
      }
    }, [data])

    return (
      <>
        <Box
          sx={{
            zIndex: 1,
            position: "absolute",
            left: s("20px"),
            top: s("20px"),
            "& > :not(:last-child)": {
              mr: s("10px"),
            },
          }}
        >
          {data &&
            "publicationLevel" in data &&
            data.publicationLevel !== "TICKET_REQUIRED" &&
            // make sure that the client finished querying the user
            currentUser !== undefined &&
            !isTicketValid(currentUser?.ticket?.validUntil) && (
              <Label
                shadow
                backgroundColor={theme.palette.white[70]}
                color={"black"}
              >
                {t(labelConfig.Free)}
              </Label>
            )}
          {data?.type === "LiveConcert" && status !== "past" && (
            <Label
              shadow
              backgroundColor={theme.palette.black.accentRed}
              color={theme.palette.white.main}
            >
              {t(
                data.liveConcertType === "PREMIERE"
                  ? labelConfig.Premiere
                  : labelConfig.Live,
              )}
            </Label>
          )}
          {index === 0 && sliderLayout === SliderLayout.WeeklyTop10 && (
            <Label
              shadow
              backgroundColor={theme.palette.primary.main}
              color={theme.palette.white.main}
            >
              {t_common("component__badge_top_ranked")}
            </Label>
          )}
        </Box>
        {/* WeeklyTop10 */}
        {data && sliderLayout === SliderLayout.WeeklyTop10 && index < 10 && (
          <Box
            sx={{
              height: s("162px"),
              width: "100%",
              background:
                "linear-gradient(0deg, rgba(10, 15, 23, 0.5) 0%, rgba(10, 15, 23, 0) 100%)",
              zIndex: 1,
              position: "absolute",
              bottom: `${height - imageHeight}px`,
              left: 0,
            }}
          >
            <DGText
              sx={{
                color: theme.palette.white.main,
                position: "absolute",
                left: s(
                  `${dimensions === TeaserDimension.WeeklyTop10 ? 15 : 5}px`,
                ),
                bottom: s(
                  `${dimensions === TeaserDimension.WeeklyTop10 ? 15 : 5}px`,
                ),
              }}
              tx={
                dimensions === TeaserDimension.WeeklyTop10
                  ? TI.new_untitled1
                  : TI.new_untitled2
              }
            >
              {(index + 1).toString().padStart(2, "0")}
            </DGText>
          </Box>
        )}
        {/* CuratedContentLink */}
        {data?.type === "CuratedContentLink" && (
          <Box
            sx={{
              position: "absolute",
              top: s("16px"),
              left: s("16px"),
              zIndex: 2,
            }}
          >
            <CuratedContentLinkIcon />
          </Box>
        )}
        {(data?.type === "CuratedContentLink" ||
          data?.type === "Genre" ||
          data?.type === "Epoch") && (
          <Box
            sx={{
              position: "absolute",
              bottom: s("11px"),
              left: s("16px"),
              zIndex: 2,
            }}
          >
            <DGText
              tx={TI.new_subheadline_bold}
              color={theme.palette.white.main}
            >
              {data.title}
            </DGText>
          </Box>
        )}
        {data ? (
          <>
            {[TeaserDimension.AvatarBox, TeaserDimension.AvatarLabel].includes(
              dimensions,
            ) ? (
              <Box
                sx={{
                  height: `${imageHeight}px`,
                  width: "100%",
                  pt: s("30px"),
                  display: "flex",
                  alignItems: "flex-end",
                  justifyContent: "center",
                }}
              >
                <Avatar
                  src={data.picture}
                  //no need to fallback on <Avatar>
                  sx={{
                    height: imageHeight,
                    width: imageHeight,
                    backgroundRepeat: "no-repeat",
                    backgroundImage: `url(${
                      data.type === "Partner"
                        ? empty
                        : data.type === "Group"
                          ? emptyGroup
                          : emptyAvatar
                    })`,
                    backgroundPosition: "center",
                    backgroundSize:
                      (data.type === "Partner"
                        ? s("240")
                        : data.type === "Group"
                          ? s("188")
                          : s("102")) + "px",
                    backgroundColor: theme.palette.secondary.main,
                  }}
                />
              </Box>
            ) : (
              <Box
                sx={{
                  position: "relative",
                  height: imageHeight,
                  overflow: "hidden",
                  isolation: "isolate",
                }}
              >
                {(data.type === "CuratedContentLink" ||
                  data.type === "Genre" ||
                  data.type === "Epoch") && (
                  <Box
                    sx={{
                      background:
                        curatedContentLinkBg[
                          data.type === "CuratedContentLink" && data.slugV2
                            ? (data.slugV2 as keyof typeof curatedContentLinkBg)
                            : data.type === "Genre"
                              ? "genres"
                              : "epochs"
                        ],
                      backgroundPosition: "center",
                      backgroundRepeat: "no-repeat",
                      backgroundSize: "cover",
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      zIndex: 0,
                    }}
                  />
                )}
                <Image
                  className={
                    data.type === "CuratedContentLink"
                      ? "teaser-api-image"
                      : undefined
                  }
                  src={data.picture}
                  hasTransparentFallback
                  sx={{
                    position: "relative",
                    zIndex: 1,
                    height:
                      imageHeight *
                      (data.type === "CuratedContentLink" ? 1.08 : 1),
                    width:
                      width * (data.type === "CuratedContentLink" ? 1.08 : 1),
                    backgroundRepeat: "no-repeat",
                    backgroundImage: `url(${empty})`,
                    backgroundPosition: "center",
                    backgroundSize: s("240px"),
                    backgroundColor: theme.palette.secondary.main,
                    mixBlendMode:
                      data.type === "CuratedContentLink" ||
                      data.type === "Genre" ||
                      data.type === "Epoch"
                        ? "multiply"
                        : undefined,
                  }}
                />
              </Box>
            )}
            {dimensions !== TeaserDimension.LandscapeSmallTeaserNoText && (
              <Box
                ref={textboxes}
                sx={{
                  pl: s("32px"),
                  pr: s("32px"),
                  pt: s("24px"),
                  justifyContent: "flex-start",
                  alignItems:
                    data.type !== "Artist" &&
                    data.type !== "Partner" &&
                    data.type !== "Group"
                      ? "flex-start"
                      : "center",
                  display: "flex",
                  flexDirection: "column",
                  position: "relative",
                  wordBreak: "break-word",
                }}
              >
                {data?.progress ? (
                  <Box
                    sx={{
                      position: "absolute",
                      width: "100%",
                      height: s("1.5px"),
                      left: 0,
                      top: s("9px"),
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    }}
                  >
                    <Box
                      sx={{
                        width: s("341px"),
                        background: "rgba(196, 196, 196, 0.1)",
                        height: s("1.5px"),
                      }}
                    >
                      <Box
                        sx={{
                          width: `${data?.progress * 341}px`,
                          background: theme.palette.tertiary.accentYellow,
                          height: s("1.5px"),
                        }}
                      />
                    </Box>
                  </Box>
                ) : null}
                <DGText
                  tx={TI.Subheadline04tv}
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "-webkit-box",
                    WebkitBoxOrient: "vertical",
                    WebkitLineClamp: data.subtitle
                      ? data.type === "Album" ||
                        dimensions === TeaserDimension.WeeklyTop10
                        ? 2
                        : 3
                      : subtitleLines,
                    color: "white",
                    textAlign:
                      data.type !== "Artist" &&
                      data.type !== "Partner" &&
                      data.type !== "Group"
                        ? undefined
                        : "center",
                    fontVariantNumeric: "oldstyle-nums",
                    fontFeatureSettings: "onum",
                  }}
                >
                  {data.title}
                </DGText>
                {data.subtitle ? (
                  <DGText
                    tx={TI.Subheadline06tv}
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      display: "-webkit-box",
                      WebkitLineClamp: subtitleLines,
                      WebkitBoxOrient: "vertical",
                      color: "white",
                      textAlign:
                        data.type !== "Artist" &&
                        data.type !== "Partner" &&
                        data.type !== "Group"
                          ? undefined
                          : "center",
                      fontVariantNumeric: "oldstyle-nums",
                      fontFeatureSettings: "onum",
                    }}
                  >
                    {data.subtitle}
                  </DGText>
                ) : null}
                {dimensions ===
                TeaserDimension.WeeklyTop10 ? null : data.liveConcert ? (
                  <>
                    <Livelabel liveConcert={data.liveConcert} />
                    <Reruns
                      isTeaser
                      tx={TI.Subheadline08tv}
                      sx={{ mt: s("4px"), color: theme.palette.white[55] }}
                      liveConcert={data.liveConcert}
                    />
                  </>
                ) : data.caption ? (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "flex-end",
                      height: s("34px"),
                    }}
                  >
                    <DGText
                      tx={TI.Subheadline09tv}
                      sx={{
                        color: theme.palette.white.grey,
                        mr: s("16px"),
                      }}
                    >
                      {data.caption}
                    </DGText>
                    {data.label && (
                      <Label
                        backgroundColor={theme.palette.black.buttonBlue}
                        color={theme.palette.white.main}
                      >
                        {data.label}
                      </Label>
                    )}
                  </Box>
                ) : data.label ? (
                  <Box
                    sx={{
                      mt: s("12px"),
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "flex-end",
                      height: s("34px"),
                    }}
                  >
                    {data.label && (
                      <Label
                        backgroundColor={theme.palette.black.buttonBlue}
                        color={theme.palette.white.main}
                      >
                        {data.label}
                      </Label>
                    )}
                  </Box>
                ) : null}
              </Box>
            )}
          </>
        ) : (
          <Box
            sx={{
              display: "flex",
              height: "100%",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <DGText
              tx={TI.Subheadline05tv}
              sx={{
                color: theme.palette.white.main,
              }}
            >
              {t("slider__show_more")}
            </DGText>
            <ChevronRight
              size={s(50)}
              fill={theme.palette.tertiary.accentYellow}
            />
          </Box>
        )}
      </>
    )
  },
  (prevProps, nextProps) =>
    _.isEqual(prevProps.data, nextProps.data) &&
    prevProps.data?.progress === nextProps.data?.progress,
)
